'use client';

import React, { useState, useMemo } from 'react';
import {
    BarChart3,
    Calendar,
    ChevronDown,
    Printer,
    FileDown,
    PlusCircle,
    ArrowUpRight,
    TrendingUp,
    ShieldCheck,
    History,
    FileCheck
} from 'lucide-react';
import Link from 'next/link';

// --- Types ---

interface EquityChangeItem {
    description: string;
    amount: number;
    type: 'initial' | 'addition' | 'reduction' | 'profit' | 'final';
    date: string;
}

// --- Mock Data ---

const MOCK_MODAL_DATA = {
    unit: 'PAUD Al-Azhar 1',
    period: 'Tahun Ajaran 2024/2025',
    initialModal: 150000000,
    additions: [
        { date: '2024-02-08', description: 'Setoran Modal Yayasan Tahap 1', amount: 50000000 },
        { date: '2024-02-15', description: 'Hibah Sarana Pendidikan khusus', amount: 10000000 },
    ],
    netProfit: 12500000, // From Income Statement
};

export default function LaporanPerubahanModalPage() {
    // --- State ---
    const [period, setPeriod] = useState('Februari 2024');

    // --- Calculations ---
    const equityReport = useMemo(() => {
        const totalAdditions = MOCK_MODAL_DATA.additions.reduce((sum, item) => sum + item.amount, 0);
        const finalModal = MOCK_MODAL_DATA.initialModal + totalAdditions + MOCK_MODAL_DATA.netProfit;

        const tableRows: EquityChangeItem[] = [
            { description: 'Modal Awal', amount: MOCK_MODAL_DATA.initialModal, type: 'initial', date: '01/02/2024' },
            ...MOCK_MODAL_DATA.additions.map(item => ({
                description: item.description,
                amount: item.amount,
                type: 'addition' as const,
                date: item.date
            })),
            { description: 'Laba / (Rugi) Bersih Periode Berjalan', amount: MOCK_MODAL_DATA.netProfit, type: 'profit', date: '29/02/2024' },
            { description: 'Modal Akhir', amount: finalModal, type: 'final', date: '29/02/2024' }
        ];

        return { tableRows, finalModal };
    }, []);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                            <Link href="/sekolah" className="hover:text-primary transition-all">Laporan Keuangan</Link>
                            <span className="text-muted/50">•</span>
                            <span className="text-foreground">Perubahan Modal</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <BarChart3 className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-black text-foreground tracking-tight whitespace-nowrap">Laporan Perubahan Modal</h1>
                                <p className="text-secondary font-medium flex items-center gap-2 text-sm uppercase tracking-wider">
                                    <ShieldCheck className="w-3.5 h-3.5 text-success" /> Posisi Ekuitas & Investasi • {MOCK_MODAL_DATA.unit}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <button className="flex items-center gap-3 px-6 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-primary/30 transition-all shadow-sm group">
                            <FileDown className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" /> Export Excel
                        </button>
                        <button className="flex items-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">
                            <Printer className="w-4 h-4" /> Export PDF
                        </button>
                    </div>
                </div>
            </section>

            {/* Filter Section */}
            <div className="bg-surface border border-border-main p-6 rounded-3xl shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div className="space-y-2 flex-1 md:max-w-[280px]">
                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Pilih Periode Laporan</label>
                        <button className="w-full flex items-center justify-between px-5 py-3.5 bg-background border border-border-main rounded-2xl text-[11px] font-bold text-foreground">
                            <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-primary" />
                                {period}
                            </div>
                            <ChevronDown className="w-4 h-4 text-muted" />
                        </button>
                    </div>
                    <div className="flex-1 flex md:justify-end items-center gap-4">
                        <div className="px-5 py-3 rounded-2xl bg-primary-soft/20 border border-primary/10 flex items-center gap-3">
                            <div className="p-1.5 bg-primary rounded-lg text-white"><FileCheck className="w-3.5 h-3.5" /></div>
                            <p className="text-[10px] font-black text-primary uppercase tracking-widest">Laporan Telah Tervalidasi</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Report Table */}
            <div className="bg-surface border border-border-main rounded-4xl overflow-hidden shadow-2xl shadow-primary/5">
                <div className="p-8 md:p-12 space-y-10">
                    <div className="text-center space-y-2">
                        <h2 className="text-2xl font-black text-foreground uppercase tracking-tight">Laporan Perubahan Ekuitas</h2>
                        <div className="flex items-center justify-center gap-2 text-muted uppercase tracking-[0.2em] text-[10px] font-bold">
                            <span>{MOCK_MODAL_DATA.unit}</span>
                            <span className="w-1.5 h-1.5 rounded-full bg-muted/30"></span>
                            <span>Periode {period}</span>
                        </div>
                    </div>

                    <div className="max-w-4xl mx-auto border border-border-main border-b-0 rounded-3xl overflow-hidden shadow-sm">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-background/50 border-b border-border-main">
                                    <th className="px-10 py-6 text-[11px] font-black text-muted uppercase tracking-widest">Keterangan Perubahan Modal</th>
                                    <th className="px-10 py-6 text-[11px] font-black text-muted uppercase tracking-widest text-right">Nominal (IDR)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border-main/50">
                                {equityReport.tableRows.map((row, idx) => {
                                    const isFinal = row.type === 'final';
                                    const isInitial = row.type === 'initial';
                                    const isProfit = row.type === 'profit';

                                    return (
                                        <tr key={idx} className={`${isFinal ? 'bg-primary/5' : ''} transition-colors group`}>
                                            <td className="px-10 py-5">
                                                <div className="flex items-center gap-4">
                                                    {isInitial && <History className="w-4 h-4 text-muted" />}
                                                    {row.type === 'addition' && <PlusCircle className="w-4 h-4 text-success" />}
                                                    {isProfit && <TrendingUp className="w-4 h-4 text-primary" />}
                                                    {isFinal && <div className="p-1.5 bg-primary rounded-lg text-white"><FileCheck className="w-3.5 h-3.5" /></div>}

                                                    <div className="space-y-0.5">
                                                        <p className={`text-sm font-bold ${isFinal ? 'text-primary' : 'text-foreground'}`}>{row.description}</p>
                                                        <p className="text-[10px] font-bold text-muted uppercase tracking-tighter">{row.date}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={`px-10 py-5 text-right font-black tabular-nums transition-all ${isFinal ? 'text-2xl text-primary' : 'text-base text-foreground'}`}>
                                                {formatCurrency(row.amount)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer Info */}
                    <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between p-8 bg-background/50 rounded-3xl border border-border-main border-dashed gap-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-success-soft text-success rounded-full flex items-center justify-center">
                                <ShieldCheck className="w-6 h-6" />
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs font-black text-foreground uppercase tracking-widest">Integritas Laporan</p>
                                <p className="text-[10px] font-medium text-muted leading-relaxed">Sistem menjamin data modal akhir adalah hasil kalkulasi otomatis <br /> dari modal awal, setoran modal, dan laba rugi berjalan.</p>
                            </div>
                        </div>
                        <div className="shrink-0">
                            <div className="bg-primary-soft/20 px-6 py-3 rounded-2xl border border-primary/20 flex items-center gap-3">
                                <ArrowUpRight className="w-4 h-4 text-primary" />
                                <span className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Ready for Audit</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
